import { useState, useCallback } from 'react';

import { FormikSelectOption } from '@models/common/FormikSelectOption';
import { CustomerViewModel } from '@models/Customer';
import { ContactService } from '@services/Contact';
import { CustomerService } from '@services/Customer';

export interface CustomerDataState {
  selectedCustomer: CustomerViewModel | null;
  customerContacts: FormikSelectOption[];
  customerBillingAddresses: FormikSelectOption[];
  customerShippingAddresses: FormikSelectOption[];
  isLoading: boolean;
}

export interface UseCustomerDataOptions {
  onError?: (message: string) => void;
  onCustomerLoaded?: (customer: CustomerViewModel) => void;
  onCustomerCleared?: () => void;
}

const emptySelectOption: FormikSelectOption[] = [{ value: '0', text: '' }];

export const useCustomerData = (options: UseCustomerDataOptions = {}) => {
  const { onError, onCustomerLoaded, onCustomerCleared } = options;

  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerViewModel | null>(null);
  const [customerContacts, setCustomerContacts] =
    useState<FormikSelectOption[]>(emptySelectOption);
  const [customerBillingAddresses, setCustomerBillingAddresses] =
    useState<FormikSelectOption[]>(emptySelectOption);
  const [customerShippingAddresses, setCustomerShippingAddresses] =
    useState<FormikSelectOption[]>(emptySelectOption);
  const [isLoading, setIsLoading] = useState(false);

  const clearCustomerData = useCallback(
    (formikProps: any) => {
      setSelectedCustomer(null);
      formikProps.setFieldValue('contactID', 0);
      formikProps.setFieldValue('contactEmail', '');
      formikProps.setFieldValue('contactPhone', '');
      formikProps.setFieldValue('billingAddressID', 0);
      formikProps.setFieldValue('isSameShipping', false);
      formikProps.setFieldValue('shippingAddressID', 0);
      formikProps.setFieldValue('isTaxExempt', false);
      setCustomerContacts(emptySelectOption);
      setCustomerBillingAddresses(emptySelectOption);
      setCustomerShippingAddresses(emptySelectOption);
      onCustomerCleared?.();
    },
    [onCustomerCleared]
  );

  const loadCustomerContacts = useCallback(
    async (customerId: number) => {
      const result = await CustomerService.getCustomerContactsSelectOptions(
        customerId
      );
      if ('data' in result) {
        const options = [{ value: '0', text: '' }].concat(
          result.data.map((row) => ({
            value: row.value,
            text: row.text,
          }))
        );
        setCustomerContacts(options);
      } else {
        const errorMessage = `Failed to get customer contacts select options. ${result.errorMessages}`;
        console.error(errorMessage);
        onError?.(errorMessage);
      }
    },
    [onError]
  );

  const loadCustomerBillingAddresses = useCallback(
    async (customerId: number) => {
      const result =
        await CustomerService.getCustomerBillingAddressesSelectOptions(
          customerId
        );
      if ('data' in result) {
        const options = [{ value: '0', text: '' }].concat(
          result.data.map((row) => ({
            value: row.value,
            text: row.text,
          }))
        );
        setCustomerBillingAddresses(options);
      } else {
        const errorMessage = `Failed to get customer billing addresses select options. ${result.errorMessages}`;
        console.error(errorMessage);
        onError?.(errorMessage);
      }
    },
    [onError]
  );

  const loadCustomerShippingAddresses = useCallback(
    async (customerId: number) => {
      const result =
        await CustomerService.getCustomerShippingAddressesSelectOptions(
          customerId
        );
      if ('data' in result) {
        const options = [{ value: '0', text: '' }].concat(
          result.data.map((row) => ({
            value: row.value,
            text: row.text,
          }))
        );
        setCustomerShippingAddresses(options);
      } else {
        const errorMessage = `Failed to get customer shipping addresses select options. ${result.errorMessages}`;
        console.error(errorMessage);
        onError?.(errorMessage);
      }
    },
    [onError]
  );

  const loadSelectedCustomer = useCallback(
    async (customerId: string, formikProps: any) => {
      if (customerId === '0') {
        clearCustomerData(formikProps);
        return;
      }

      setIsLoading(true);
      const result = await CustomerService.getById(Number(customerId));

      if (result.isSuccess && 'data' in result) {
        const customer = result.data;
        setSelectedCustomer(customer);

        // Set form values from customer
        formikProps.setFieldValue('customerID', customer.id);
        formikProps.setFieldValue('isTaxExempt', customer.isTaxExempt);
        formikProps.setFieldValue(
          'contactID',
          customer.primaryContact?.id || 0
        );
        formikProps.setFieldValue(
          'contactEmail',
          customer.primaryContact?.email || ''
        );
        formikProps.setFieldValue(
          'contactPhone',
          customer.primaryContact?.phone || ''
        );
        formikProps.setFieldValue('billingAddressID', customer.billingID || 0);
        formikProps.setFieldValue('isSameShipping', customer.isSameShipping);
        formikProps.setFieldValue(
          'shippingAddressID',
          customer.shippingID || 0
        );

        // Load related data in parallel
        await Promise.all([
          loadCustomerContacts(Number(customerId)),
          loadCustomerBillingAddresses(Number(customerId)),
          loadCustomerShippingAddresses(Number(customerId)),
        ]);

        onCustomerLoaded?.(customer);
      } else {
        const errorMessage = `Failed to get the selected customer record. ${result.errorMessages}`;
        console.error(errorMessage);
        onError?.(errorMessage);
      }

      setIsLoading(false);
    },
    [
      clearCustomerData,
      loadCustomerContacts,
      loadCustomerBillingAddresses,
      loadCustomerShippingAddresses,
      onCustomerLoaded,
      onError,
    ]
  );

  const loadSelectedContact = useCallback(
    async (contactId: string, formikProps: any) => {
      if (contactId === '0') {
        formikProps.setFieldValue('contactEmail', '');
        formikProps.setFieldValue('contactPhone', '');
        return;
      }

      const result = await ContactService.getById(Number(contactId));
      if (result.isSuccess && 'data' in result) {
        formikProps.setFieldValue('contactEmail', result.data.email || '');
        formikProps.setFieldValue('contactPhone', result.data.phone || '');
      } else {
        const errorMessage = `Failed to get the selected contact details. ${result.errorMessages}`;
        console.error(errorMessage);
        onError?.(errorMessage);
      }
    },
    [onError]
  );

  // For edit forms: initialize customer data from existing record
  const initializeCustomerData = useCallback(
    async (customerId: number, _contactId?: number) => {
      if (customerId === 0) {
        return;
      }

      setIsLoading(true);

      const [customerResult] = await Promise.all([
        CustomerService.getById(customerId),
        loadCustomerContacts(customerId),
        loadCustomerBillingAddresses(customerId),
        loadCustomerShippingAddresses(customerId),
      ]);

      if (customerResult.isSuccess && 'data' in customerResult) {
        setSelectedCustomer(customerResult.data);
      }

      setIsLoading(false);
    },
    [
      loadCustomerContacts,
      loadCustomerBillingAddresses,
      loadCustomerShippingAddresses,
    ]
  );

  return {
    // State
    selectedCustomer,
    customerContacts,
    customerBillingAddresses,
    customerShippingAddresses,
    isLoading,

    // Actions
    loadSelectedCustomer,
    loadSelectedContact,
    clearCustomerData,
    initializeCustomerData,

    // Setters for external updates
    setSelectedCustomer,
    setCustomerContacts,
    setCustomerBillingAddresses,
    setCustomerShippingAddresses,
  };
};
